<template>
  <div class="tenancy-view-info-wrapper">
    <div class="row main-content">
      <div class="basic-column">
        <div class="column-header">
          {{ $t('tenancy.basicInfo') }}
        </div>
        <div class="contents">
          <div class="column-content">
            <TextField
              :label=" $t('tenancy.companyId')"
              :value="isDummyCompany ? '--' : deep(currentTenancy, 'company.id')"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.companyName')"
              :value="deep(currentTenancy, 'company.name')"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.tradeName')"
              :value="currentTenancy.tradeName || '--'"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.phoneNumber')"
              :value="deep(currentTenancy, 'company.phone') || '--'"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.alternativePhoneNumber')"
              :value="deep(currentTenancy, 'company.altPhone') || '--'"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.faxNumber')"
              :value="deep(currentTenancy, 'company.fax') || '--'"
              :showColon="false"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.email')"
              :value="deep(currentTenancy, 'company.email') || '--'"
              :showColon="false"
              :readonly="true"
            />
          </div>
          <div class="column-content column-content-right">
            <div
              v-if="adminAccount && adminAccount.TenancyAccount"
              class="main-contacts"
            >
              <label>{{ $t('tenancy.mainContact') }}</label>
              <p>{{ displayI18nFullName(adminAccount.TenancyAccount.contactFirstName, adminAccount.TenancyAccount.contactLastName) }}</p>
              <p v-if="adminAccount.TenancyAccount.phone">
                {{ adminAccount.TenancyAccount.phone }}
              </p>
              <p
                v-if="adminAccount.username"
                class="main-contact-username"
              >
                {{ adminAccount.username }}
              </p>
            </div>
            <div
              v-if="!showHolidayHour"
              class="tenancy-working-hours"
            >
              <label>{{ $t('tenancy.workingHours') }}</label>
              <ul
                v-if="regularOpeningHours.length"
                class="tenancy-working-hours-list"
              >
                <li
                  v-for="(item, index) in regularOpeningHours"
                  :key="index"
                >
                  <span>{{ item.workingDayFrom | customWorkingDayLabel }}</span>
                  <span v-if="item.workingDayTo"> - </span>
                  <span class="u-margin-right-small">{{ item.workingDayTo | customWorkingDayLabel }}</span>
                  <span>{{ item.workingHourFrom | customWorkingHourLabel }}</span>
                  <span v-if="item.workingHourTo"> - </span>
                  <span>{{ item.workingHourTo | customWorkingHourLabel }}</span>
                </li>
              </ul>
              <p v-else>
                --
              </p>
            </div>

            <div
              v-if="isOffice && canReadDir && enableDirectoryFeature"
              class="directory-link"
            >
              <div
                v-if="showDirPin"
                class="pin-placeholder"
              >
                <div class="pin__bubble-orange">
                  <i
                    class="fa fa-thumb-tack"
                    aria-hidden="true"
                  />
                </div>
              </div>
              <a @click="() => navToDirectory()">{{ $t('directory.requisition') }}</a>
            </div>
          </div>
        </div>
        <TenancyOpeningHour v-if="showHolidayHour" />
        <div class="alarm-section">
          <div class="fire-alarm-section">
            <div class="alarm-section__label-btn">
              <label>{{ $t('tenancy.alarmSysHint') }}</label>
            </div>
            <label class="emphasis alarm-label">{{ $t('tenancy.fireAlarmSystem') }}:</label>
            <input
              v-hkldinput
              class="alarm-checkbox"
              type="radio"
              name="fireAlarm"
              :disabled="true"
              :checked="currentTenancy.isFireAlarm"
            >
            <label class="alarm-checkbox-label">{{ $t('action.yes') }}</label>
            <input
              v-hkldinput
              class="alarm-checkbox"
              type="radio"
              name="fireAlarm"
              :disabled="true"
              :checked="currentTenancy.isFireAlarm !== null && !currentTenancy.isFireAlarm"
            >
            <label class="alarm-checkbox-label">{{ $t('action.no') }}</label>
          </div>
          <div>
            <label class="emphasis alarm-label">{{ $t('tenancy.burglarAlarmSystem') }}:</label>
            <input
              v-hkldinput
              type="radio"
              name="burglarAlarm"
              :disabled="true"
              :checked="currentTenancy.isBurglarAlarm"
            >
            <label class="alarm-checkbox-label">{{ $t('action.yes') }}</label>
            <input
              v-hkldinput
              type="radio"
              name="burglarAlarm"
              :disabled="true"
              :checked="currentTenancy.isBurglarAlarm !== null && !currentTenancy.isBurglarAlarm"
            >
            <label class="alarm-checkbox-label">{{ $t('action.no') }}</label>
          </div>
        </div>
      </div>
      <div class="contact-column">
        <div class="column-header">
          {{ $t('tenancy.premisesInformation') }}
        </div>
        <div class="contents">
          <div class="site-building-floor">
            <TextField
              :label=" $t('tenancy.building')"
              :value="currentTenancy.building && currentTenancy.building.code || $t('emptyValue')"
              :showColon="false"
              class="building"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.zone')"
              :value="zone"
              :showColon="false"
              class="zone"
              :readonly="true"
            />
            <TextField
              :label=" $t('tenancy.type')"
              :value="currentTenancyType"
              :showColon="false"
              :readonly="true"
            />
            <div
              v-if="isCurrentRegionHK"
              class="ekey-info"
            >
              <TextField
                v-if="!isTenant"
                :label=" $t('tenancy.pm-t006')"
                :value="currentTenancy.eKey || $t('emptyValue')"
                :showColon="false"
                :readonly="true"
              />
            </div>
          </div>
          <div class="premises-info">
            <TextField
              :label=" $t('tenancy.premises')"
              :value="currentLeases || $t('emptyValue')"
              :showColon="false"
              :readonly="true"
            />
            <ul class="premises-details">
              <li class="title">
                <span class="detail">{{ $t('tenancy.premisesDetail') }}</span>
                <span class="floor">{{ $t('tenancy.floor') }}</span>
                <span class="effective-date">{{ $t('tenancy.effectiveDate') }}</span>
                <span class="effective-date">{{ $t('tenancy.endDate') }}</span>
              </li>
              <li
                v-for="(info, index) in premisesDetailList"
                :key="index"
              >
                <span class="detail">{{ info.detail }}</span>
                <span class="floor">{{ info.floor }}</span>
                <span class="effective-date">{{ formatDate(info.effectiveDate) }}</span>
                <span class="effective-date">{{ formatDate(info.endDate) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <TenancyMoveInOut
      v-if="hasReadMoveInOutAccess"
      :tenancyId="tenancyId"
      :hotbs="hotbs"
    />
    <div
      v-if="enableTenantProjectsFeature"
      class="hotb-section"
    >
      <div
        v-show="showCreateHoButton()"
        class="hotb-btns rows"
      >
        <button
          v-hkldreset
          type="button"
          class="btn btn--orange"
          @click="() => createHandover()"
        >
          {{ $t('action.new') }} {{ $t('tenancyProject.handover') }}
        </button>
        <button
          v-hkldreset
          type="button"
          class="btn btn--darkblue"
          @click="() => createTakeback()"
        >
          {{ $t('action.new') }} {{ $t('tenancyProject.takeback') }}
        </button>
      </div>
      <HotbList
        v-if="showHoList()"
        :tenancyId="tenancyId"
      />
    </div>
    <TenancyDocumentsVue v-if="canTenancyRecordRead" />

    <modal
      ref="alertModalForNewHandover"
      modalId="alert-modal-for-new-handover"
    >
      <div slot="content">
        <p class="modal-pure-text">
          {{ $t('handover.noBuildingHint', [missingType, missingTips]) }}
        </p>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange"
          @click="() => closeAlertModal()"
        >
          {{ $t('hint.okay') }}
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Modal from '@/controls/Modal'
import TextField from '@/controls/TextField'
import HotbList from '@/modules/hotb/HotbList'
import formFullnameMixin from '@/pages/formFullnameMixin'
import { aclHelper, tmsPermsTypes, ccdPermsTypes } from '@/utils/aclHelper'
import { companyType, hotbTypes, tenancyType } from '@/utils/locale'
import { DIR_STATUS } from '@/modules/directories/consts'
import { setTenancyInfoForDirectory } from '@/modules/directories/utils'
import dateUtil from '@/utils/dateUtil'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import ordinalUtils from '@/utils/ordinalUtils'
import { regionNames } from '@/utils/consts'
import { concatLeasesAsPremises } from '@/modules/ccd/tenancy/utils'
import TenancyOpeningHour from './TenancyOpeningHour'
import TenancyMoveInOut from './TenancyMoveInOut'
import TenancyDocumentsVue from './TenancyDocuments.vue'

export default {
  name: 'TenancyViewInformation',
  components: {
    TenancyDocumentsVue,
    TextField,
    HotbList,
    Modal,
    TenancyMoveInOut,
    TenancyOpeningHour,
  },
  mixins: [formFullnameMixin],
  props: {
    tenancyId: {
      type: [String, Number],
      default: undefined,
    },
  },
  data () {
    return {
      deep,
      hotbTypes,
      bannedStatus: ['CANCELLED', 'COMPLETED'],
      missingType: '',
      missingTips: '',
      hotbs: [],
      submittedDirs: [],
      submittedDirsLoaded: false,
    }
  },

  computed: {
    ...mapGetters('ccd', ['currentTenancy']),
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    ...mapGetters('directories', ['canCurrUserApprove']),
    ...mapGetters('regions', ['backEndRegionalEnv', 'enableTenantProjectsFeature', 'enableDirectoryFeature', 'currentRegion']),
    isCurrentRegionHK () {
      return this.currentRegion.name === regionNames.HK
    },
    currentTenancyType () {
      return this.currentTenancy.type
        ? this.$t(`tenancy.${this.currentTenancy.type.toLowerCase()}`)
        : this.$t('emptyValue')
    },
    currentLeases () {
      const concatPremises = concatLeasesAsPremises(this.currentTenancy) || this.$t('emptyValue')
      return concatPremises
    },
    showHolidayHour () {
      return !this.isOffice && lodash.get(this.backEndRegionalEnv, 'moduleSwitch.holidayWorkingHours')
    },
    canReadDir () {
      return this.isTenant || (this.isEngineer && aclHelper.getAccess(tmsPermsTypes.directory.read))
    },
    zone () {
      return (this.currentTenancy.sections && this.currentTenancy.sections.length > 0)
        ? this.currentTenancy.sections.join(', ')
        : this.$t('emptyValue')
    },
    adminAccount () {
      if (this.currentTenancy && this.currentTenancy.accounts) {
        const account = this.currentTenancy.accounts.find(account => {
          return lodash.get(account, 'TenancyAccount.isAdmin')
        })
        if (account) {
          return JSON.parse(JSON.stringify(account))
        } else {
          return {}
        }
      }
      return {}
    },
    premisesDetailList () {
      const buildingFloors = lodash.get(this.currentTenancy, 'building.floor', [])
      const list = lodash.cloneDeep(lodash.get(this.currentTenancy, 'premisesDetailList', []))
      list.sort((a, b) => {
        return buildingFloors.indexOf(a.floor) - buildingFloors.indexOf(b.floor)
      })
      return list
    },
    regularOpeningHours () {
      return lodash.get(this.currentTenancy, 'openingHours.regularWorkingHours') || []
    },
    isDummyCompany () {
      return lodash.get(this.currentTenancy, 'company.type') === companyType.D
    },
    isOffice () {
      return lodash.get(this.currentTenancy, 'type') === tenancyType.Office
    },
    hasReadMoveInOutAccess () {
      return aclHelper.getAccess(ccdPermsTypes.moveInOut.read)
    },
    canTenancyRecordRead () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.read)
    },
    showDirPin () {
      return this.submittedDirs.length !== 0 && this.canCurrUserApprove
    },
  },
  watch: {
    currentTenancy (val) {
      if (val) this.loadSubmittedDirs()
    },
  },
  async mounted () {
    this.loadSubmittedDirs()
  },
  methods: {
    ...mapActions('hotb', ['createHotb']),
    ...mapActions('directories', { loadAllDirectories: 'loadAll' }),
    ...mapMutations('directories', {
      setTenancyInfo: mutationTypes.DIRECTORY_TENANCY_INFO,
    }),

    ordinalNumber (number) {
      return ordinalUtils.getOrdinalFor(Number(number), true)
    },
    formatDate (val) {
      return dateUtil.formatDate(val)
    },
    navToDirectory () {
      this.setTenancyInfo(
        setTenancyInfoForDirectory(this.currentTenancy))
      navigation.toPage('tenancyDirectories', { id: this.currentTenancy.id })
    },
    async createHandover (createType) {
      if (!this.currentTenancy.building) {
        this.missingType = this.$t('tenancy.building')
        this.missingTips = this.$t('tenancy.inputBuilding')
        this.init()
      } else if (lodash.isEmpty(this.currentTenancy.premisesDetailList)) {
        this.missingType = this.$t('tenancy.floor')
        this.missingTips = this.$t('tenancy.addFloorDetail')
        this.init()
      } else {
        const id = await this.createHotb({ tenancy: this.currentTenancy, type: createType || hotbTypes.HANDOVER })
        navigation.toPage('handover', { id })
      }
    },

    async createTakeback () {
      await this.createHandover(hotbTypes.TAKEBACK)
    },

    showCreateHoButton () {
      return aclHelper.getAccess(tmsPermsTypes.hotb.create)
    },

    showHoList () {
      return aclHelper.getAccess(tmsPermsTypes.hotb.read)
    },

    openAlertModal () {
      this.alertModal.open()
    },

    closeAlertModal () {
      this.alertModal.close()
    },
    async init () {
      this.alertModal = this.$refs.alertModalForNewHandover
      this.openAlertModal()
    },
    async loadSubmittedDirs () {
      if (!this.submittedDirsLoaded && this.isOffice && this.canReadDir && this.enableDirectoryFeature) {
        const tenancyId = this.$router.currentRoute.params.id
        this.submittedDirs = await this.loadAllDirectories({
          tenancyId,
          status: DIR_STATUS.SUBMITTED,
        })
        this.submittedDirsLoaded = true
      }
    },
  },
  beforeDestroyed () {
    if (this.alertModal) {
      this.alertModal.destroy()
    }
    $('#alertModalForNewHandover').remove()
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/components/tenancy-view-info';
@import "~styles/stickyBtn";
@import '~styles/table';
@import '~styles/popup';
@import '~styles/multi-value';
</style>

<style lang="scss" scoped>
.ekey-info {
  width: 500px;
  word-break: break-all;
}
.tenancy-view-info-wrapper {
  .main-content {
    display: flex;
    min-height: 400px;
    div.text-field label {
      height: 20px;
      padding: 0 0 5px 0;
      margin: 0;
      font-size: 14px;
    }
    .contents {
      padding: 15px;
      // height: calc(100% - 48px);
      display: flex;
      label {
        line-height: 1;
      }
      p {
        word-break: break-word;
        line-height: 1;
      }
      .column-content {
        flex: 1;
        border: none;
      }
      .text-field {
        margin-bottom: 10px;
      }
    }

    .directory-link {
      display: flex;
      align-items: center;
      margin-top: 10px;
      a {
        text-decoration: underline;
        font-weight: bold;
      }
    }
    .basic-column {
      flex: 1;
      background: white;
      box-shadow: 0 1px 5px rgba(gray, .2);
      flex-direction: column;
      .column-header {
        height: 48px;
        min-height: 48px;
        line-height: 48px;
        padding-left: 15px;
      }

    }
    .alarm-section {
      padding: 15px;
      padding-top: 5px;
      border-top: 1px solid #f1f1f1;
      &__label-btn {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 10px;
      }
      &__btn {
        text-decoration: underline;
        color: #0c3a58;
        font-weight: bold;
      }
      .alarm-label {
        display: inline;
        margin-right: 10px;
        font-weight: normal;
      }
      .alarm-checkbox-label {
        margin: 0px;
        font-weight: normal;
      }
    }
    .column-content{
      padding: 0;
      margin: 0;
      .tenancy-working-hours {
        label {
          margin-top: 0;
        }
      }
    }
    .column-content-right {
      padding-left: 15px;
      .main-contacts {
        label {
          margin: 0;
          line-height: 1;
          height: 20px;
        }
        margin-bottom: 20px;
      }
    }
    .main-contact-username {
      word-break: break-word;
      word-wrap: break-word;
    }
    .contact-column {
      width: -webkit-fill-available;
      flex: 1;
      margin-left: 15px;
      background: white;
      display: flex;
      flex-direction: column;
      .column-header {
        height: 48px;
        line-height: 48px;
        padding-left: 15px;
        font-size: 14px;
        font-weight: bold;
      }
      .contact-section-header {
        font-size: 14px;
        font-weight: bold;
        p {
          margin: 0;
        }
      }
      .column-content {
        padding: 0 15px;
        flex: 1;
      }

      .contents {
        flex-direction: column;
      }
      .site-building-floor {
        .text-field {
          display: inline-block;
          margin-right: 100px;
        }
      }

      .premises-info {
        ul.premises-details {
          margin: 0;
          list-style: none;
          li {
            display: flex;
            span {
              display: inline-block;
            }
            .detail {
              flex: 1;
              text-align: left;
            }
            .floor {
              flex-basis: 40px;
            }
            .effective-date {
              flex-basis: 130px;
              text-align: right;
            }
          }
          li:first-child {
            margin-bottom: 5px;
          }
          li:not(:first-child) {
            background-color: #fafbfc;
            margin-bottom: 5px;
          }
          li.title {
            span {
                height: 15px;
                font-family: Verdana;
                font-size: 12px;
                letter-spacing: 0;
                color: #878787;
            }
          }
        }
      }
    }
  }
  .hotb-section {
    background-color: #ffffff;
    box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
    width: 100%;
    overflow: hidden;
    .hotb-btns {
      margin-left: 15px !important;
      margin-bottom: 15px;
      button:last-child {
        margin-left: 15px;
      }
    }
  }
}

#alertModalForNewHandover {
  width: 520px;
  .confirm-header, .modal-header {
    font-size: 18px;
    text-align: left;
    color: #0c3a58;
    margin-bottom: 20px;
    line-height: 1.5;
  }
}

tr:nth-child(even) {
  background-color: #ffffff;
  border: 1px solid #ededed;
}
tr:nth-child(odd) {
  background-color: #f8f8f8
}
</style>
